import { Injectable, inject, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  defer,
  distinctUntilChanged,
  from,
  merge,
  of,
  switchMap,
  tap,
} from "rxjs";
import { ICustomer } from "../shared/models/customer.interface";
import { IApiResult } from "../shared/models/apiresult.interface";
import { DataService } from "../data.service";
import { MatDialog } from "@angular/material/dialog";
import { NewCustomerDialogComponent } from "../dialogs/customer-dialog/customer-dialog.component";
import { ToastrService } from "ngx-toastr";
import { db } from "src/app/db.service";
import { SyncService } from "../sync.service";
import { FormBuilder } from "@angular/forms";

@Injectable({
  providedIn: "root",
})
export class CustomersService {
  _formBuilder = inject(FormBuilder);
  _dataService = inject(DataService);
  _syncService = inject(SyncService);
  http = inject(HttpClient);
  toastr = inject(ToastrService);
  dialog = inject(MatDialog);
  selectedCustomerId: number;
  chipSelected: BehaviorSubject<string> = new BehaviorSubject("");
  dataLoading = signal(false);
  selectedCustomerGroups = this._formBuilder.group({});
  sumDebit: number = this._dataService.getLS("sumDebit") || 0;
  sumKredit: number = this._dataService.getLS("sumKredit") || 0;
  sumDebitSig = signal(this.sumDebit);
  sumKreditSig = signal(this.sumKredit);
  customersFiltered = signal(false);

  baseUrl: string = "https://tekoplast.az/hesabat/api.php/records/customers/";

  constructor() {
    let formControls = {};
    this._dataService.customerGroups().forEach((customerGroup) => {
      formControls[customerGroup.id] = false;
    });
    this.selectedCustomerGroups = this._formBuilder.group(formControls);
  }

  customerDialog(customerData?) {
    let data = null;
    if (customerData) {
      if (customerData?.customerId) {
        data = customerData
          ? this._dataService
              .customers()
              .find((c) => c.id == customerData?.customerId)
          : null;
        // CUSTOMER DEBIT CREIT DATA BELOW
        data = {
          ...data,
          customerData: customerData,
        };
      }
      if (customerData?.customerGroupId)
        data = {
          ...data,
          customerGroupId: customerData?.customerGroupId,
        };
    }

    const dialogRef = this.dialog.open(NewCustomerDialogComponent, {
      width: data ? "800px" : "500px",
      data: { data: data },
      closeOnNavigation: true,
      disableClose: this._dataService.mobileDevice
        ? false
        : customerData
        ? false
        : true,
    });
    dialogRef.afterClosed().subscribe(
      (res) => {
        if (res) {
          this.selectedCustomerId = res.id;
        }
      },
      () => {
        this.toastr.error("Xəta baş verdi. Zəhmət olmasa yenidən cəhd edin");
      }
    );
  }

  getCustomersBalance(filtered?: boolean) {
    if (this._dataService.endDate) {
      const localData = this._dataService.getLS("debitorsCreditors") || [];
      const localObservable = defer(() => of(localData));

      const combinedObservable = combineLatest([
        this._dataService.dateChanged,
        this._syncService.newDataSynced.pipe(distinctUntilChanged()),
      ]).pipe(
        tap(() => {
          this.dataLoading.set(true);
        }),
        switchMap(() => {
          const startDate = new Date(
            this._dataService.startDate?.value?.format("YYYY-MM-DD") || new Date().toISOString().split('T')[0]
          );
          const endDate = this._dataService.endDate?.value?.format("YYYY-MM-DD") || new Date().toISOString().split('T')[0];

          let filters = null;
          if (filtered) {
            filters = this._dataService.getLS(
              `${this._dataService.dbTable}_filteredCustomerGroups`
            );
          }

          // --- Define current date and one month ago for payment due check ---
          const currentDate = new Date();
          const oneMonthAgo = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() - 1,
            currentDate.getDate()
          );

          return from(
            Promise.all([
              db.stock.where("date").belowOrEqual(endDate).toArray(),
              db.sells.where("date").belowOrEqual(endDate).toArray(),
              db.bank.where("date").belowOrEqual(endDate).toArray(), // These are all bank transactions up to endDate
            ]).then(([stocks, sells, bank]) => {
              const customersList = this._dataService.customers();
              const customerGroupsMap = new Map(
                this._dataService.customerGroups().map((cg) => [cg.id, cg.name])
              );
              const bankGroupsMap = new Map(
                this._dataService
                  .bankGroups()
                  .map((bg) => [bg.id?.toString(), bg])
              );

              // --- Pre-calculate last payment date for each customer from 'bank' data ---
              const customerLastPaymentDateMap = new Map<number, Date>();
              bank.forEach((tx) => {
                if (tx.customerId && (Number(tx.income) || 0) > 0) {
                  const txDate = new Date(tx.date);
                  const existingLastDate = customerLastPaymentDateMap.get(
                    tx.customerId
                  );
                  if (!existingLastDate || txDate > existingLastDate) {
                    customerLastPaymentDateMap.set(tx.customerId, txDate);
                  }
                }
              });

              const customerData: CustomerData = {};
              customersList.forEach((customerItem) => {
                customerData[customerItem.id] = {
                  customerId: customerItem.id,
                  customerName: customerItem.name || "",
                  rawCustomerGroupId: customerItem.customerGroupId,
                  rawCustomerOpeningBalance: customerItem.balance || 0,
                  customerType: customerItem.customerGroupId
                    ? customerGroupsMap.get(customerItem.customerGroupId) ||
                      null
                    : null,
                  customerGroupId: customerItem.customerGroupId, // Keep this for filters compatibility if needed
                  sellsTotal: 0,
                  sellsBefore: 0,
                  incomeTotal: 0,
                  incomeBefore: 0,
                  outgoingTotal: 0,
                  outgoingBefore: 0,
                  stocksTotal: 0,
                  stocksBefore: 0,
                  incomeWithoutBalance: 0,
                  outgoingWithoutBalance: 0,
                };
              });

              const processData = (dataToProcess: any[], type: string) => {
                dataToProcess.forEach((item) => {
                  if (item.customerId && customerData[item.customerId]) {
                    const currentCustomerAggregates =
                      customerData[item.customerId];
                    const itemDate = new Date(item.date);
                    const isAfterStartDate = itemDate >= startDate;
                    const quantity = Number(item.quantity) || 0;
                    const price = Number(item.price) || 0;
                    const value = quantity * price;

                    switch (type) {
                      case "stock":
                        currentCustomerAggregates.stocksTotal +=
                          isAfterStartDate &&
                          value >= 0 &&
                          item.tType !== "Production"
                            ? value
                            : 0;
                        currentCustomerAggregates.sellsTotal +=
                          isAfterStartDate &&
                          value < 0 &&
                          item.tType !== "Production"
                            ? -value
                            : 0;
                        currentCustomerAggregates.stocksBefore +=
                          !isAfterStartDate &&
                          value >= 0 &&
                          item.tType !== "Production"
                            ? value
                            : 0;
                        currentCustomerAggregates.sellsBefore +=
                          !isAfterStartDate &&
                          value < 0 &&
                          item.tType !== "Production"
                            ? -value
                            : 0;
                        break;
                      case "sell":
                        currentCustomerAggregates.sellsTotal +=
                          isAfterStartDate && value >= 0 ? value : 0;
                        currentCustomerAggregates.stocksTotal +=
                          isAfterStartDate && value < 0 ? -value : 0;
                        currentCustomerAggregates.sellsBefore +=
                          !isAfterStartDate && value >= 0 ? value : 0;
                        currentCustomerAggregates.stocksBefore +=
                          !isAfterStartDate && value <= 0 ? -value : 0;
                        break;
                      case "bank":
                        const bankGroupFromMap: any = bankGroupsMap.get(
                          item.bankGroupId?.toString()
                        );
                        const income = Number(item.income) || 0;
                        const outgoings = Number(item.outgoings) || 0;
                        if (income > 0) {
                          currentCustomerAggregates.incomeTotal +=
                            isAfterStartDate ? income : 0;
                          currentCustomerAggregates.incomeWithoutBalance +=
                            isAfterStartDate &&
                            bankGroupFromMap?.includeInBalance
                              ? income
                              : 0;
                          currentCustomerAggregates.incomeBefore +=
                            !isAfterStartDate &&
                            bankGroupFromMap?.includeInBalance
                              ? income
                              : 0;
                        } else if (outgoings > 0) {
                          currentCustomerAggregates.outgoingTotal +=
                            isAfterStartDate ? outgoings : 0;
                          currentCustomerAggregates.outgoingWithoutBalance +=
                            isAfterStartDate &&
                            bankGroupFromMap?.includeInBalance
                              ? outgoings
                              : 0;
                          currentCustomerAggregates.outgoingBefore +=
                            !isAfterStartDate &&
                            bankGroupFromMap?.includeInBalance
                              ? outgoings
                              : 0;
                        }
                        break;
                    }
                  }
                });
              };

              this.sumDebit = 0;
              this.sumKredit = 0;

              processData(stocks, "stock");
              processData(sells, "sell");
              processData(bank, "bank");

              Object.values(customerData).forEach((data) => {
                data.stocksTotal = parseFloat(data.stocksTotal.toFixed(2));
                data.sellsTotal = parseFloat(data.sellsTotal.toFixed(2));
                data.incomeTotal = parseFloat(data.incomeTotal.toFixed(2));
                data.outgoingTotal = parseFloat(data.outgoingTotal.toFixed(2));
                data.stocksBefore = parseFloat(data.stocksBefore.toFixed(2));
                data.sellsBefore = parseFloat(data.sellsBefore.toFixed(2));
                data.incomeBefore = parseFloat(data.incomeBefore.toFixed(2));
                data.outgoingBefore = parseFloat(
                  data.outgoingBefore.toFixed(2)
                );
                data.incomeWithoutBalance = parseFloat(
                  data.incomeWithoutBalance.toFixed(2)
                );
                data.outgoingWithoutBalance = parseFloat(
                  data.outgoingWithoutBalance.toFixed(2)
                );
              });

              const filteredResult = Object.values(customerData);
              const result = filteredResult.map((data) => {
                const balanceWithoutB =
                  data.sellsTotal -
                  data.stocksTotal -
                  data.incomeWithoutBalance +
                  data.outgoingWithoutBalance;
                const balanceInPeriod =
                  data.sellsTotal -
                  data.stocksTotal -
                  data.incomeTotal +
                  data.outgoingTotal;
                const balanceAtPeriodStart =
                  data.sellsBefore -
                  data.stocksBefore -
                  data.incomeBefore +
                  data.outgoingBefore +
                  data.rawCustomerOpeningBalance;
                const finalBalance = parseFloat(
                  (balanceAtPeriodStart + (balanceWithoutB || 0)).toFixed(2)
                );

                // --- Calculate paymentTimeIsDue ---
                let paymentTimeIsDue = false;
                const lastPaymentDate = customerLastPaymentDateMap.get(
                  data.customerId
                );
                if (finalBalance > 0) {
                  // Only consider "due" if the customer owes money
                  if (lastPaymentDate) {
                    // If there was a payment
                    if (lastPaymentDate < oneMonthAgo) {
                      paymentTimeIsDue = true; // Payment is old
                    }
                  } else {
                    // No payment transaction found (within data up to endDate)
                    paymentTimeIsDue = true; // Owes money and no payment record
                  }
                }
                // --- End of paymentTimeIsDue calculation ---

                if (!filtered || !filters?.length) {
                  if (finalBalance >= 0) {
                    this.sumDebit += finalBalance;
                  } else {
                    this.sumKredit += finalBalance;
                  }
                } else {
                  if (finalBalance >= 0) {
                    this.sumDebit +=
                      filters.length && filters.includes(data.customerGroupId)
                        ? finalBalance
                        : 0;
                  } else {
                    this.sumKredit +=
                      filters.length && filters.includes(data.customerGroupId)
                        ? finalBalance
                        : 0;
                  }
                }

                return {
                  ...data,
                  customerGroupId: data.rawCustomerGroupId,
                  earlyBalance: parseFloat(
                    data.rawCustomerOpeningBalance.toFixed(2)
                  ),
                  balance: parseFloat(balanceInPeriod?.toFixed(2) || 0),
                  balanceBefore: parseFloat(balanceAtPeriodStart.toFixed(2)),
                  finalBalance,
                  paymentTimeIsDue, // New key added here
                };
              });

              this._dataService.setLS(
                "sumDebit",
                parseFloat(this.sumDebit.toFixed(2))
              );
              this._dataService.setLS(
                "sumKredit",
                parseFloat(this.sumKredit.toFixed(2))
              );
              this.sumDebitSig.set(parseFloat(this.sumDebit.toFixed(2)));
              this.sumKreditSig.set(parseFloat(this.sumKredit.toFixed(2)));
              this._dataService.setLS("debitorsCreditors", result.slice(0, 50));
              return result;
            })
          );
        }),
        tap(() => {
          setTimeout(() => {
            this.dataLoading.set(false);
          }, 500);
        })
      );

      return merge(localObservable, combinedObservable);
    }
  }

  async getCustomersBalanceById(customerId) {
    if (this._dataService.endDate) {
      const combinedObservable = combineLatest([
        this._dataService.dateChanged,
        this._syncService.newDataSynced.pipe(distinctUntilChanged()),
        this.chipSelected.pipe(distinctUntilChanged()),
      ]).pipe(
        tap(() => {
          this.dataLoading.set(true);
        }),
        switchMap(async ([dateChanged, newDataSynced, chipSelected]) => {
          let startDate = new Date(
            this._dataService.startDate?.value?.format("YYYY-MM-DD") || new Date().toISOString().split('T')[0]
          );

          const [stocks, sells, bank] = await Promise.all([
            db.stock.where("customerId").equals(customerId).toArray(),
            db.sells.where("customerId").equals(customerId).toArray(),
            db.bank.where("customerId").equals(customerId).toArray(),
          ]);

          const customerData: CustomerData = {};

          customerData[customerId] = {
            customerId: customerId,
            sellsTotal: 0,
            sellsBefore: 0,
            incomeTotal: 0,
            incomeBefore: 0,
            outgoingTotal: 0,
            outgoingBefore: 0,
            stocksTotal: 0,
            stocksBefore: 0,
          };

          const processData = (data, type) => {
            data.forEach((item) => {
              if (item.customerId) {
                const isAfterStartDate = new Date(item.date) >= startDate;
                const value = item.quantity * item.price;

                switch (type) {
                  case "stock":
                    if (customerData[customerId]) {
                      customerData[customerId].stocksTotal +=
                        isAfterStartDate &&
                        value >= 0 &&
                        item.tType != "Production"
                          ? value
                          : 0;
                      customerData[customerId].sellsTotal +=
                        isAfterStartDate &&
                        value < 0 &&
                        item.tType != "Production"
                          ? -value
                          : 0;
                      customerData[customerId].stocksBefore +=
                        !isAfterStartDate &&
                        value >= 0 &&
                        item.tType != "Production"
                          ? value
                          : 0;
                      customerData[customerId].sellsBefore +=
                        !isAfterStartDate &&
                        value < 0 &&
                        item.tType != "Production"
                          ? -value
                          : 0;
                    }
                    break;

                  case "sell":
                    if (customerData[customerId]) {
                      customerData[customerId].sellsTotal +=
                        isAfterStartDate && value >= 0 ? value : 0;
                      customerData[customerId].stocksTotal +=
                        isAfterStartDate && value < 0 ? -value : 0;

                      customerData[customerId].sellsBefore +=
                        !isAfterStartDate && value >= 0 ? value : 0;
                      customerData[customerId].stocksBefore +=
                        !isAfterStartDate && value <= 0 ? -value : 0;
                    }
                    break;

                  case "bank":
                    if (customerData[customerId]) {
                      if (
                        item.income > 0 &&
                        this._dataService
                          .bankGroups()
                          .find(
                            (b) =>
                              b.id.toString() === item.bankGroupId.toString()
                          )?.includeInBalance
                      ) {
                        customerData[customerId].incomeTotal += isAfterStartDate
                          ? item.income
                          : 0;
                        customerData[customerId].incomeBefore +=
                          !isAfterStartDate ? item.income : 0;
                      } else if (
                        item.outgoings > 0 &&
                        this._dataService
                          .bankGroups()
                          .find(
                            (b) =>
                              b.id.toString() === item.bankGroupId.toString()
                          )?.includeInBalance
                      ) {
                        customerData[customerId].outgoingTotal +=
                          isAfterStartDate ? item.outgoings : 0;
                        customerData[customerId].outgoingBefore +=
                          !isAfterStartDate ? item.outgoings : 0;
                      }
                    }
                    break;
                }
              }
            });
          };

          processData(stocks, "stock");
          processData(sells, "sell");
          processData(bank, "bank");

          Object.values(customerData).forEach((data) => {
            data.stocksTotal = parseFloat(data.stocksTotal.toFixed(2));
            data.sellsTotal = parseFloat(data.sellsTotal.toFixed(2));
            data.incomeTotal = parseFloat(data.incomeTotal.toFixed(2));
            data.outgoingTotal = parseFloat(data.outgoingTotal.toFixed(2));
          });

          const filteredResult = Object.values(customerData);

          let result = [];
          filteredResult.forEach((data) => {
            let customer = this._dataService
              .customers()
              .find((c) => c.id == data.customerId);
            let balance =
              data.sellsTotal -
              data.stocksTotal -
              data.incomeTotal +
              data.outgoingTotal;
            let b = customer && customer?.balance ? customer.balance : 0; // balance
            let balanceBefore =
              data.sellsBefore -
              data.stocksBefore -
              data.incomeBefore +
              data.outgoingBefore +
              b;
            let pushData = {
              ...data,
              customerGroupId: customer?.customerGroupId || null,
              customerType: customer?.customerGroupId
                ? this._dataService
                    .customerGroups()
                    .find((c) => c.id == customer?.customerGroupId)?.name
                : null,
              earlyBalance: parseFloat(
                ((data.earlyBalance || 0) + (customer?.balance || 0)).toFixed(2)
              ),
              balance: parseFloat(balance?.toFixed(2) || 0),
              balanceBefore: parseFloat(balanceBefore.toFixed(2)),
              finalBalance: parseFloat(
                (balanceBefore + (balance || 0)).toFixed(2)
              ),
            };
            result.push(pushData);
          });

          return result;
        }),
        tap(() => {
          setTimeout(() => {
            this.dataLoading.set(false);
          }, 500);
        })
      );

      return combinedObservable;
    }
  }

  async getCustomerOperations(customerId?, showAll?: boolean) {
    // Get non-Bank transactions (Sells, Return, Stock Entry, etc.)
    let transactions = !showAll
      ? await db.transactions
          .where("customerId")
          .equals(customerId)
          .and((transaction) => {
            return (
              transaction.type !== "Bank" &&
              transaction.date >=
                (this._dataService.startDate?.value?.format("YYYY-MM-DD") || '1970-01-01') &&
              transaction.date <=
                (this._dataService.endDate?.value?.format("YYYY-MM-DD") || '2099-12-31')
            );
          })
          .toArray()
      : await db.transactions
          .where("customerId")
          .equals(customerId)
          .and((transaction) => transaction.type !== "Bank")
          .toArray();
    
    // Get ALL bank transactions for this customer from bank table
    const allBanks = !showAll
      ? await db.bank
          .where("customerId")
          .equals(customerId)
          .and((bank) => {
            return (
              bank.date >=
                (this._dataService.startDate?.value?.format("YYYY-MM-DD") || '1970-01-01') &&
              bank.date <=
                (this._dataService.endDate?.value?.format("YYYY-MM-DD") || '2099-12-31')
            );
          })
          .toArray()
      : await db.bank.where("customerId").equals(customerId).toArray();
    
    const bankGroupsMap = new Map(
      this._dataService.bankGroups().map((bg) => [bg.id?.toString(), bg])
    );
    
    allBanks.forEach((b: any) => {
      const bankGroup: any = bankGroupsMap.get(b.bankGroupId?.toString());
      transactions.push({
        ...b,
        id: b.id,
        tId: b.tId,
        type: "Bank",
        sum: b.income ? b.income : -b.outgoings,
        includeInBalance: bankGroup?.includeInBalance ?? true,
        date: b.date,
        dateAdded: b.dateAdded || b.date,
      });
    });
    return transactions;
  }

  getCustomerFilters() {
    const filtersMap = new Map();
    const unfiltersMap = new Map();
    let customerFilters = this._dataService.getLS(
      `${this._dataService.dbTable}_customerFilters`
    );
    if (customerFilters?.length) {
      customerFilters.forEach((cGrp) => {
        filtersMap.set(cGrp.id, cGrp.filter);
        unfiltersMap.set(cGrp.id, cGrp.unfilter ?? false);
      });
    }

    let kGrps = this._dataService
      .customerGroups()
      .map((customerGrp) => ({ ...customerGrp, filter: false }));
    let res = [];
    kGrps.forEach((custGrp) => {
      let fltr = filtersMap.get(custGrp.id);
      let unflt = unfiltersMap.get(custGrp.id);
      if (fltr || unflt) this.customersFiltered.set(true);
      res.push({ ...custGrp, filter: fltr ?? false, unfilter: unflt ?? false });
    });
    return res;
  }

  getCustomers(): Observable<IApiResult> {
    return this.http.get<IApiResult>(this.baseUrl);
  }

  getCustomerById(id: number) {
    return this.http.get(
      "https://tekoplast.az/hesabat/api.php/records/stock?join=customersorder=date,desc&join=materials&order=id,desc&filter=partnerId,eq," +
        id
    );
  }

  addCustomer(customer: ICustomer) {
    return this.http.post(this.baseUrl, customer);
  }

  updateCustomer(id: number, customer: ICustomer) {
    return this.http.put(this.baseUrl + id, customer);
  }

  deleteCustomer(id: number) {
    return this.http.delete(this.baseUrl + id);
  }
}

interface CustomerData {
  customerId?: number;
  customerName?: string;
  sellsTotal?: number;
  sellsBefore?: number;
  incomeTotal?: number;
  incomeBefore?: number;
  outgoingTotal?: number;
  outgoingBefore?: number;
  stocksTotal?: number;
  stocksBefore?: number;
  [key: string]: any;
}
